import {Component, Object3D, PhysXComponent, TextComponent} from '@wonderlandengine/api';
import {vec3} from 'gl-matrix';
import {SoundManagerInstance, Sounds} from '../managers/soundmanager.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {ElevatorGate} from './elevator-gate.js';
import {TextFade} from './text-fade.js';
import {GameManagerInstance, SceneNames} from '../managers/gamemanager.js';
import {delay} from '../utils/delay.js';
import {Blink} from './blink.js';
import {Locomotion} from './locomotion.js';
/**
 * start-elevator
 */
export class StartElevator extends Component {
    static TypeName = 'start-elevator';

    @property.object()
    playerPhysxObject?: Object3D;

    @property.object()
    elevator?: Object3D;

    @property.object()
    elevatorGate?: Object3D;

    @property.object()
    textObj01?: Object3D;

    @property.object()
    textObj02?: Object3D;

    @property.object()
    blinkObject?: Object3D;

    @property.object({required: true})
    locomotionObject!: Object3D;

    private lerpTime = 0;
    private opening = true;
    private isLerpFinished = false;
    private startLoc = vec3.create();
    private endLoc = vec3.create();
    private currentPos = vec3.create();
    private lerpRatio = 0;
    private lerpResult = vec3.create();
    private playerPhysxComponent?: PhysXComponent;
    private blinkComponent?: Blink;
    private locomotionComponent?: Locomotion;

    start() {
        if (!this.playerPhysxObject) {
            throw new Error(
                'StartElevator: playerPhysxObject not set in start-elevator component'
            );
        }
        const tempPlayerPhysxComponent =
            this.playerPhysxObject.getComponent(PhysXComponent);
        if (!tempPlayerPhysxComponent) {
            throw new Error(
                'StartElevator: playerPhysxComponent not set in start-elevator component'
            );
        }
        this.playerPhysxComponent = tempPlayerPhysxComponent;

        if (!this.blinkObject) {
            throw new Error(
                `start-elevator(${this.object.name}): blinkObject is not defined`
            );
        }
        const tempBlink = this.blinkObject.getComponent(Blink);
        if (!tempBlink) {
            throw new Error(
                `start-elevator(${this.object.name}): blinkObject does not have a Blink`
            );
        }
        this.blinkComponent = tempBlink;

        const tempLocomotion = this.locomotionObject.getComponent(Locomotion);
        if (!tempLocomotion) {
            throw new Error(
                `start-elevator(${this.object.name}): locomotionObject does not have a Locomotion Component`
            );
        }
        this.locomotionComponent = tempLocomotion;
    }

    onActivate() {
        GameManagerInstance.onSceneReady.add(this.sceneReady.bind(this));
    }
    onDeactivate() {
        GameManagerInstance.onSceneReady.remove(this.sceneReady.bind(this));
    }

    sceneReady(scene: SceneNames) {
        if (scene !== SceneNames.Scene1) {
            return;
        }

        this.blinkComponent!.startBlink(
            null,
            () => {
                this.textFadeSequence().catch((err) => console.error(err));
            },
            true,
            2
        ).catch((err) => console.error(err));
        setTimeout(() => {
            SoundManagerInstance.playSound(Sounds.elevatorStart);
            SoundManagerInstance.playSound(Sounds.elevatorLoop, true);

            /* Elevator Movement Lerp */
            this.lerpTime = 0;
            this.startLoc = [-94.0, 110.0, -438.694];
            this.endLoc = [-94.0, 895.0, -438.694];
            this.isLerpFinished = false;
            /* Text */
        }, 1000);
    }

    update(dt: number) {
        if (!this.elevator) {
            console.warn('Elevator not set in start-elevator component');
            return;
        }

        /* Elevator Up Over time */
        this.lerpTime += dt;
        this.lerpRatio = this.lerpTime / 20; /* 20 seconds */
        if (this.lerpTime < 20) {
            this.locomotionComponent!.disableRoomscaleCorrection = true;
            this.lerpResult = vec3.lerp(
                vec3.create(),
                this.startLoc,
                this.endLoc,
                this.lerpRatio
            );
            this.elevator.setPositionLocal(this.lerpResult);
        } else if (!this.isLerpFinished) {
            this.isLerpFinished = true; /* flag to be called only once */
            this.onLerpFinish(); // Call onLerpFinish when lerp is finished
        }
    }

    onLerpFinish() {
        /* Elevator reached its destination */

        if (!this.elevatorGate) {
            console.warn('ElevatorGate not set in start-elevator component');
            return;
        }
        const physxComponent = this.elevatorGate.getComponent(PhysXComponent);
        if (physxComponent) {
            physxComponent.active = true;
        }
        const elevatorGate = this.elevatorGate.getComponent(ElevatorGate);
        if (elevatorGate) {
            elevatorGate.active = true;
        }

        /* Tutorial Text FadeIn */
        if (!this.textObj02) {
            console.warn('textObj02 not set in start-elevator component');
            return;
        }
        const textFade = this.textObj02.getComponent(TextFade);
        if (textFade) {
            textFade.fadeIn();
        }

        this.locomotionComponent!.disableRoomscaleCorrection = false;

        SoundManagerInstance.playSound(Sounds.elevatorStop);
        SoundManagerInstance.stopSound(Sounds.elevatorLoop);
    }

    async textFadeSequence() {
        if (!this.textObj01) {
            console.warn('textObj01 not set in start-elevator component');
            return;
        }
        await delay(3);
        /* FadeIn/Out "1971" */
        const textComponent = this.textObj01.getComponent(TextComponent);
        const textFade = this.object.getComponent(TextFade);
        if (textComponent && textFade) {
            textComponent.text = '1971';

            textFade.fadeIn();
            SoundManagerInstance.playSound(Sounds.boom1, false, 0.5);
            await delay(3);
            textFade.fadeOut();

            await delay(3);
            /* FadeIn/Out "CHICAGO" */
            textComponent.text = 'CHICAGO';
            textFade.fadeIn();
            SoundManagerInstance.playSound(Sounds.boom4, false, 0.5);
            await delay(3);
            textFade.fadeOut();
        }
    }
}
